import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { ONE_INCH_WIDTH, PDF_BACKGROUND_COLOR, PDF_OFFSCREEN_LEFT, PDF_OFFSCREEN_WIDTH } from './statement-pdf.theme';

type Html2CanvasOptions = Parameters<typeof html2canvas>[1];

type ChunkPdfOptions = {
    filename: string;

    // jsPDF
    format?: 'letter' | 'a4';
    orientation?: 'landscape' | 'portrait';
    unit?: 'in';

    // margins in inches: [top, right, bottom, left]
    margin?: [number, number, number, number];

    // html2canvas
    canvasScale?: number;
    backgroundColor?: string;
    useCORS?: boolean;

    // chunking
    rowsPerChunk?: number;

    // DOM container for hidden rendering
    containerZIndex?: number;
};

const defaultOpt: Required<Omit<ChunkPdfOptions, 'filename'>> = {
    format: 'letter',
    orientation: 'landscape',
    unit: 'in',
    margin: [0.1, 0.1, 0.1, 0.1],
    canvasScale: 1.5,
    backgroundColor: PDF_BACKGROUND_COLOR,
    useCORS: true,
    rowsPerChunk: 250,
    containerZIndex: -1,
};

function chunkArray<T>(arr: T[], size: number): T[][] {
    if (!arr?.length) return [[]];
    const out: T[][] = [];
    for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
    return out;
}

async function waitForFontsAndImages(root: HTMLElement): Promise<void> {
    if (document.fonts?.ready) {
        await document.fonts.ready;
    }

    // Images inside root
    const imgs = Array.from(root.querySelectorAll('img'));
    await Promise.all(
        imgs.map(img => {
            if (img.complete) return Promise.resolve();
            return new Promise<void>(res => {
                img.addEventListener('load', () => res(), { once: true });
                img.addEventListener('error', () => res(), { once: true });
            });
        }),
    );

    // Give layout a tick
    await new Promise(r => requestAnimationFrame(() => r(null)));
}

function pxPerInch(): number {
    const div = document.createElement('div');
    div.style.width = ONE_INCH_WIDTH;
    div.style.position = 'absolute';
    div.style.left = PDF_OFFSCREEN_LEFT;
    document.body.appendChild(div);
    const ppi = div.getBoundingClientRect().width || 96;
    div.remove();
    return ppi;
}

/**
 * Render one HTML chunk into the provided pdf instance.
 * It will slice a (bounded) canvas into multiple PDF pages if needed.
 */
async function addHtmlChunkToPdf(params: {
    pdf: jsPDF;
    html: string;
    opt: Required<ChunkPdfOptions>;
    isFirstChunk: boolean;
    headerOnlyOnFirstChunk: boolean;
}): Promise<void> {
    const { pdf, html, opt, isFirstChunk, headerOnlyOnFirstChunk } = params;

    // Hidden container in DOM so CSS in <style> inside HTML applies
    const wrapper = document.createElement('div');
    wrapper.style.position = 'fixed';
    wrapper.style.left = PDF_OFFSCREEN_LEFT;
    wrapper.style.top = '0';
    wrapper.style.width = PDF_OFFSCREEN_WIDTH; // stable layout width (tune if needed)
    wrapper.style.zIndex = String(opt.containerZIndex);
    wrapper.style.background = opt.backgroundColor;
    wrapper.innerHTML = html;

    // For continuation chunks, strip everything except the Statement card
    // so we don't repeat header/patient/provider/claims/payments on every chunk.
    if (!isFirstChunk && headerOnlyOnFirstChunk) {
        const container = wrapper.querySelector('.container');
        if (container) {
            const cards = Array.from(container.querySelectorAll('.card'));
            if (cards.length > 0) {
                // Keep only the last card (the Statement card) and remove the rest
                const lastCard = cards[cards.length - 1] as HTMLElement;
                for (let i = 0; i < cards.length - 1; i++) {
                    const card = cards[i];
                    card.parentElement?.removeChild(card);
                }
            }
        }
    }

    document.body.appendChild(wrapper);
    await waitForFontsAndImages(wrapper);

    const wrapperRect = wrapper.getBoundingClientRect();
    const rowEls = Array.from(wrapper.querySelectorAll('table tbody tr')) as HTMLElement[];
    const rowBottomsDomPx = rowEls
        .map(tr => tr.getBoundingClientRect().bottom - wrapperRect.top)
        .filter(v => v > 0)
        .sort((a, b) => a - b);

    const canvas = await html2canvas(wrapper, {
        scale: opt.canvasScale,
        useCORS: opt.useCORS,
        backgroundColor: opt.backgroundColor,
        scrollX: 0,
        scrollY: 0,
        logging: false,
    } as Html2CanvasOptions);

    const pageWidthIn = pdf.internal.pageSize.getWidth();
    const pageHeightIn = pdf.internal.pageSize.getHeight();
    const [mt, mr, mb, ml] = opt.margin;

    const usableWIn = pageWidthIn - ml - mr;
    const usableHIn = pageHeightIn - mt - mb;

    const ppi = pxPerInch();
    const usableWPxAt1x = usableWIn * ppi;
    const scaleToPdf = usableWPxAt1x / canvas.width;
    const usableHPxAt1x = usableHIn * ppi;
    const sliceHeightPx = Math.floor(usableHPxAt1x / scaleToPdf);

    // Map DOM Y coordinates into canvas space; html2canvas uses a uniform scale factor.
    const domToCanvasScaleY = canvas.height / wrapperRect.height || 1;
    const rowBottomsCanvasPx = rowBottomsDomPx.map(y => Math.round(y * domToCanvasScaleY));

    const bleedPx = Math.ceil(2 * opt.canvasScale);
    const minSlicePx = Math.floor(sliceHeightPx * 0.5);
    // If the remaining tail of the canvas would be very small, fold it into the previous slice
    // instead of creating an almost-empty trailing page that only shows a bottom border.
    const minTailPx = Math.floor(sliceHeightPx * 0.25);

    let startY = 0;
    let pageIndex = 0;

    while (startY < canvas.height) {
        const idealEnd = Math.min(canvas.height, startY + sliceHeightPx);
        const remainingAfterIdeal = canvas.height - idealEnd;
        const isLastSlice = remainingAfterIdeal <= minTailPx;
        const safeEndLimit = isLastSlice ? canvas.height : Math.max(startY + 1, idealEnd - bleedPx);

        let cutY = 0;

        if (isLastSlice) {
            const remainingTotal = canvas.height - startY;
            const hasRowsBelowStart = rowBottomsCanvasPx.some(y => y > startY + 1);
            // If there are no more table rows below and the remaining area is tiny,
            // skip creating an extra trailing page that would only contain borders/background.
            if (!hasRowsBelowStart && remainingTotal <= minTailPx) {
                break;
            }
            // Otherwise consume the rest of the canvas in this slice.
            cutY = canvas.height;
        } else {
            for (let i = rowBottomsCanvasPx.length - 1; i >= 0; i--) {
                const y = rowBottomsCanvasPx[i];
                if (y <= safeEndLimit && y > startY + minSlicePx) {
                    cutY = y;
                    break;
                }
            }

            if (!cutY) cutY = idealEnd;
        }

        if (cutY <= startY) {
            if (isLastSlice) {
                // Nothing meaningful left; avoid creating an extra blank-ish page.
                break;
            }
            cutY = Math.min(canvas.height, startY + sliceHeightPx);
        }

        if (!isFirstChunk || pageIndex > 0) {
            pdf.addPage();
        }

        const sliceH = cutY - startY;
        const sliceCanvas = document.createElement('canvas');
        sliceCanvas.width = canvas.width;
        sliceCanvas.height = sliceH;

        const ctx = sliceCanvas.getContext('2d');
        if (ctx) {
            ctx.drawImage(canvas, 0, startY, canvas.width, sliceH, 0, 0, canvas.width, sliceH);
            const imgData = sliceCanvas.toDataURL('image/jpeg', 0.98);
            const sliceHIn = (sliceH * scaleToPdf) / ppi;
            pdf.addImage(imgData, 'JPEG', ml, mt, usableWIn, sliceHIn, undefined, 'FAST');
        }

        startY = cutY;
        pageIndex++;
    }

    wrapper.remove();
}

/**
 * Main entry:
 * - You pass base patientDetails
 * - Provide a function that can generate HTML given modified patientDetails
 * - Chunk the selected list(s)
 */
export async function generateChunkedPdf<T extends { statementsList: any[] }>(params: {
    baseData: T;
    makeHtml: (data: T, info: { chunkIndex: number; totalChunks: number }) => string;
    filename: string;
    rowsPerChunk?: number;
    // if true: only statement table is chunked; other sections are shown only in the first chunk
    headerOnlyOnFirstChunk?: boolean;
    jsPdf?: Partial<Pick<ChunkPdfOptions, 'format' | 'orientation' | 'unit' | 'margin'>>;
    html2canvas?: Partial<Pick<ChunkPdfOptions, 'canvasScale' | 'backgroundColor' | 'useCORS'>>;
}): Promise<void> {
    const {
        baseData,
        makeHtml,
        filename,
        rowsPerChunk = defaultOpt.rowsPerChunk,
        headerOnlyOnFirstChunk = true,
        jsPdf = {},
        html2canvas = {},
    } = params;

    const opt: Required<ChunkPdfOptions> = {
        ...defaultOpt,
        filename,
        ...jsPdf,
        ...html2canvas,
    };

    const pdf = new jsPDF({
        unit: opt.unit,
        format: opt.format,
        orientation: opt.orientation,
        compress: true,
    });

    const chunks = chunkArray(baseData.statementsList || [], rowsPerChunk);
    const totalChunks = chunks.length;

    for (let idx = 0; idx < totalChunks; idx++) {
        const isFirst = idx === 0;

        const chunkData = {
            ...baseData,
            statementsList: chunks[idx],
            // optional: hide other sections after the first chunk (your template must support it if you want this fully)
            // If you don't want template changes, just keep everything the same for each chunk.
        } as T;

        const html = makeHtml(chunkData, { chunkIndex: idx, totalChunks });

        await addHtmlChunkToPdf({
            pdf,
            html,
            opt,
            isFirstChunk: isFirst,
            headerOnlyOnFirstChunk,
        });
    }

    pdf.save(filename);
}
